import Image from 'next/image';
import Link from 'next/link';
import type { FC, PropsWithChildren } from 'react';

import RobotImg from '@/assets/drawing-illustrations/robot-reading-book.webp';
import type { Industry } from '@/services/resume-checker';

import Button from '../Button';
import Icon from '../Icon';
import Text from '../Text';
import { Dialog, DialogContent, DialogTrigger } from '../ui/dialog';

const ApplyReviewDialog: FC<
  PropsWithChildren<{
    offerLink: string;
    className?: string;
    industry?: Industry;
  }>
> = ({ children, offerLink, className, industry }) => {
  const advantages = [
    'Catch all mistakes and fix them instantly',
    'Fix gaps recruiters notice immediately',
    'Stand out from hundreds of applicants',
    'Create an ATS-friendly Resume that gets past filters',
  ];

  return (
    <Dialog>
      <DialogTrigger className={className}>{children}</DialogTrigger>
      <DialogContent className="overflow-hidden p-0">
        <div className="relative flex flex-col items-center gap-6 p-8 pt-6">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-100 via-white to-primary-200 opacity-60" />
          <div className="relative flex w-full flex-col items-center gap-6">
            <div className="flex aspect-square h-40 items-center justify-center overflow-hidden rounded-full bg-white">
              <Image
                src={RobotImg}
                alt="MindPal CV assistant"
                className="h-full w-full object-cover"
              />
            </div>

            <div className="flex flex-col items-center gap-2 text-center">
              <Text variant="jb-h2" className="text-jb-primary-500">
                Your resume deserves better
              </Text>
              <Text
                variant="jb-body-large"
                className="max-w-sm text-jb-neutral-800"
              >
                <strong>Before you apply!</strong> Let our AI review your Resume
                and tailor it to this job offer in seconds.
              </Text>
            </div>

            <div className="flex w-full flex-col items-center gap-2 rounded-xl border border-primary-200 bg-white/70 p-4 backdrop-blur-sm">
              {advantages.map((point) => (
                <div
                  key={point}
                  className="flex w-full flex-row items-center gap-3"
                >
                  <Icon
                    icon="material-symbols:check-circle"
                    className="rounded-full text-jb-primary-500"
                  />
                  <Text
                    variant="jb-body-medium"
                    className="text-jb-neutral-900"
                  >
                    {point}
                  </Text>
                </div>
              ))}
            </div>

            <div className="flex w-full flex-col gap-2">
              <Link
                href={{
                  pathname: '/builder',
                  query: { ...(industry && { industry }) },
                }}
              >
                <Button variant="jbPrimary" className="w-full" tabIndex={-1}>
                  Review my resume with AI
                </Button>
              </Link>
              <a target="_blank" href={offerLink}>
                <Button
                  variant="unstyled"
                  className="w-full text-center text-jb-neutral-700"
                >
                  Apply without reviewing
                </Button>
              </a>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ApplyReviewDialog;
